import { Bell, BellOff, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useNotifications, type AppName } from '@/app/hooks/useNotifications';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover";
import { Button } from '@/app/components/ui/button';
import { Switch } from '@/app/components/ui/switch';
import { Label } from '@/app/components/ui/label';

interface NotificationCenterProps {
  appName: AppName;
  appDisplayName: string;
  apiUrl: string;
  authToken: string;
  userEmail?: string | null;
}

export function NotificationCenter({
  appName,
  appDisplayName,
  apiUrl,
  authToken,
  userEmail
}: NotificationCenterProps) {
  const {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    toggleNotifications,
    testNotification,
    isLocalMode
  } = useNotifications(appName, apiUrl, authToken, userEmail);

  const isInPreview = window.location.hostname.includes('figma.site') || 
                      window.location.hostname.includes('figmaiframepreview');

  if (!isSupported) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all group">
          {isEnabled ? (
            <Bell className={`w-5 h-5 ${isLocalMode ? 'text-amber-500' : 'text-blue-600'}`} />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          )}
          {isEnabled && (
            <span className={`absolute top-2 right-2 w-2 h-2 ${isLocalMode ? 'bg-amber-400' : 'bg-blue-500'} rounded-full border-2 border-white`}></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 overflow-hidden rounded-2xl border-gray-200 shadow-2xl" align="end">
        <div className="bg-slate-50 border-b border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cog6ToothIcon className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Centro de Notificações</h3>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {isLocalMode && isEnabled && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-amber-900">Modo de Compatibilidade</p>
                  <p className="text-[10px] leading-relaxed text-amber-800/80">
                    O browser não permitiu o registo do Service Worker. As notificações funcionarão apenas com a app aberta.
                  </p>
                </div>
              </div>
            </div>
          )}
          {isInPreview && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-amber-900">Modo Figma Preview</p>
                  <p className="text-[10px] leading-relaxed text-amber-800/80">
                    Notificações push em background requerem o domínio real (aqlapps.com). No preview apenas notificações locais funcionam.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
            <div className="space-y-0.5">
              <Label className="text-sm font-bold text-slate-900">{appDisplayName}</Label>
              <p className="text-[11px] text-slate-500">Alertas em tempo real e background</p>
            </div>
            <Switch 
              checked={isEnabled} 
              onCheckedChange={toggleNotifications}
              disabled={isLoading || permission === 'denied'}
            />
          </div>

          {permission === 'denied' && (
            <p className="text-[10px] text-red-500 font-medium bg-red-50 p-2 rounded-lg border border-red-100">
              ⚠️ Notificações bloqueadas pelo navegador. Por favor desbloqueie nas definições do site.
            </p>
          )}

          {isEnabled && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[10px] text-emerald-600 font-bold bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-100">
                <ShieldCheck className="w-3.5 h-3.5" />
                Sincronizado com o ecossistema AQL
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full text-xs font-bold text-slate-600 border-slate-200 hover:bg-slate-50 h-9 rounded-lg"
                onClick={testNotification}
              >
                Enviar teste de ligação
              </Button>
            </div>
          )}
        </div>

        <div className="p-3 bg-slate-50 border-t border-gray-100 text-center">
          <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest">AQL APPS Ecosystem · 2026</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
